import type { Guide } from "./guide";

/** Guide 8: routines in plain words or on a call, one per user, their runs, and pausing one. */
export const ROUTINES: Guide = {
  slug: "routines",
  samples: "routines",
  title: "Routines that run themselves",
  nav: "Routines",
  summary:
    "Asks that run by themselves: on a schedule in plain words, or whenever your system calls, one for each user.",
  minutes: 10,
  lede: "A routine is an ask that runs by itself: on a schedule in plain words, or whenever your system calls. Each user can have their own.",
  lines: ["routines", "events"],
  steps: [
    {
      id: "every",
      title: "Say when, in plain words",
      body: [
        "Words like `weekday at 08:00` or `mon, wed, fri at 18:30`, read in the user's own time zone ([`routines`](/developers/routines)).",
      ],
      doors: {
        ts: { sample: "guide-routines-every" },
        py: { sample: "guide-routines-every" },
        cli: { sample: "guide-routines-every" },
        http: { sample: "guide-routines-every" },
        mcp: { sample: "routines" },
      },
      then: "Bash answers with the cron it made and the next run. Each run lands in the routine's own conversation.",
    },
    {
      id: "call",
      title: "Or run on a call",
      body: [
        "For when something happens in your system, such as a late train.",
      ],
      doors: {
        ts: { sample: "guide-routines-call" },
        py: { sample: "guide-routines-call" },
        cli: { sample: "guide-routines-call" },
        http: { sample: "guide-routines-call" },
        mcp: { sample: "guide-routines-call" },
      },
      then: "Each call runs it once. Your payload rides along as data, never as instructions.",
    },
    {
      id: "each",
      title: "Give each user their own",
      body: ["A routine per user, from one loop."],
      doors: {
        ts: { sample: "guide-routines-each" },
        py: { sample: "guide-routines-each" },
        cli: { sample: "guide-routines-each" },
        http: { sample: "guide-routines-each" },
        mcp: {
          note: "An agent adds one at a time with `routines_add`, naming each `user`.",
        },
      },
      then: "`08:00` is each user's own morning, in the time zone you [named them](/developers/guides/many-customers) with.",
    },
    {
      id: "runs",
      title: "Read its runs",
      body: ["Each run is an ask: its status, and the conversation it ran in."],
      doors: {
        ts: { sample: "guide-routines-runs" },
        py: { sample: "guide-routines-runs" },
        cli: { sample: "guide-routines-runs" },
        http: { sample: "guide-routines-runs" },
        mcp: {
          note: "Runs are read from an SDK, the CLI or HTTP. An agent lists routines with `routines_list`.",
        },
      },
      then: "`routine.fired` reaches your webhooks each time one runs.",
    },
    {
      id: "pause",
      title: "Pause it",
      body: ["Paused, it skips its runs until you resume it."],
      doors: {
        ts: { sample: "guide-routines-pause" },
        py: { sample: "guide-routines-pause" },
        cli: { sample: "guide-routines-pause" },
        http: { sample: "guide-routines-pause" },
        mcp: { note: "Pausing is done from an SDK, the CLI or HTTP." },
      },
      then: "Resuming says when it runs next.",
      prints: "Paused rtn_5kd.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: [
        "One for each user, one on a call, its runs and a pause, in one program.",
      ],
      doors: {
        ts: { sample: "guide-routines-whole" },
        py: { sample: "guide-routines-whole" },
        cli: { sample: "guide-routines-whole" },
        http: { sample: "guide-routines-whole" },
        mcp: {
          note: "An agent adds routines with `routines_add` and lists them with `routines_list`.",
        },
      },
      then: "Bash runs each one by itself, and your webhooks hear every run.",
    },
  ],
};
